import React from "react";
import { Link } from "react-router-dom";
import useSEO from "@/hooks/useSEO";

const CONTACT_EMAIL = "privacy@proofmark.app";
const LAST_UPDATED = "2 July 2026";

export default function PrivacyPolicy() {
  useSEO({
    title: "Privacy Policy — ProofMark",
    description:
      "How ProofMark collects, uses, and protects personal data — legal basis, retention, sub-processors, cookies, and your GDPR rights.",
    path: "/privacy-policy",
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/80 px-6 py-4 flex items-center gap-3">
        <Link to="/" className="font-serif-display text-lg text-foreground hover:opacity-80 transition-opacity">
          ProofMark
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="font-serif-display text-3xl mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-12">Last updated: {LAST_UPDATED}</p>

        <section className="space-y-10 text-sm leading-relaxed text-muted-foreground">

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">1. Who we are</h2>
            <p>
              ProofMark is an enterprise AI knowledge governance platform operated by CENTAUR.
              References to &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;ProofMark&rdquo; in this policy mean the operator of this service.
              You can contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-foreground underline underline-offset-2">{CONTACT_EMAIL}</a>.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">2. What data we collect</h2>
            <p className="mb-3">We collect the following categories of data when you use ProofMark:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-foreground">Account data</strong> — email address, display name, and hashed password when you create an account or are provisioned as a user.</li>
              <li><strong className="text-foreground">Usage data</strong> — pages visited, actions taken within the application, and session information. Collected via PostHog analytics (see section 6).</li>
              <li><strong className="text-foreground">Communications</strong> — email address and any message content when you contact us via the waitlist form or support channels.</li>
              <li><strong className="text-foreground">Technical data</strong> — IP address, browser type, and approximate location for security purposes (login rate limiting, abuse prevention).</li>
            </ul>
            <p className="mt-3">
              We do not collect payment card data directly. We do not collect or store the operational knowledge artifacts your organization reviews — that data remains in your environment or your connected source systems.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">3. How we use your data</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>To provide and operate the ProofMark platform</li>
              <li>To send transactional emails (password reset, account notifications)</li>
              <li>To improve the product through aggregated usage analytics</li>
              <li>To detect and prevent abuse, unauthorized access, and security threats</li>
              <li>To contact you about product updates if you have joined the waitlist (you can opt out at any time)</li>
            </ul>
            <p className="mt-3">
              We do not sell your data. We do not use your data to train AI models.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">4. Legal basis for processing (GDPR)</h2>
            <p className="mb-3">If you are located in the European Economic Area (EEA) or United Kingdom, we process your personal data under the following legal bases:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-foreground">Contract</strong> — processing necessary to provide the service you have requested</li>
              <li><strong className="text-foreground">Legitimate interests</strong> — security monitoring, fraud prevention, product improvement</li>
              <li><strong className="text-foreground">Consent</strong> — session recording and optional analytics where we have asked for your consent</li>
            </ul>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">5. Data retention</h2>
            <p>
              Account data is retained for the duration of your account plus 30 days after deletion.
              Usage analytics are retained for 12 months. Password reset tokens expire after 1 hour.
              Security logs (login attempts) are held in memory only and are not persisted to disk.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">6. Cookies and analytics</h2>
            <p className="mb-3">
              We use <strong className="text-foreground">PostHog</strong> for product analytics. PostHog is configured to:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Track page views and in-app actions using a pseudonymous identifier</li>
              <li>Record sessions only if you have explicitly consented via our cookie banner</li>
            </ul>
            <p className="mt-3">
              You can withdraw consent for session recording at any time by clearing your browser&apos;s local storage for this site or by contacting us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-foreground underline underline-offset-2">{CONTACT_EMAIL}</a>.
              Basic page-view analytics continue without session recording and do not require consent under most EU interpretations (anonymized, no cross-site tracking).
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">7. Data sharing</h2>
            <p className="mb-3">We share data only with the following sub-processors:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-foreground">Railway</strong> — cloud hosting (EU and US regions)</li>
              <li><strong className="text-foreground">PostHog</strong> — product analytics (US-hosted, EU data residency available on request)</li>
              <li><strong className="text-foreground">Brevo (Sendinblue)</strong> — transactional email</li>
            </ul>
            <p className="mt-3">
              We do not share your data with any other third parties without your explicit consent, except where required by law.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">8. Your rights</h2>
            <p className="mb-3">Under GDPR and equivalent laws, you have the right to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-foreground">Access</strong> — request a copy of the personal data we hold about you</li>
              <li><strong className="text-foreground">Rectification</strong> — correct inaccurate data</li>
              <li><strong className="text-foreground">Erasure</strong> — request deletion of your account and personal data</li>
              <li><strong className="text-foreground">Portability</strong> — receive your data in a structured, machine-readable format</li>
              <li><strong className="text-foreground">Objection</strong> — object to processing based on legitimate interests</li>
              <li><strong className="text-foreground">Withdraw consent</strong> — withdraw any consent you have given at any time</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, email <a href={`mailto:${CONTACT_EMAIL}`} className="text-foreground underline underline-offset-2">{CONTACT_EMAIL}</a>.
              We will respond within 30 days.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">9. Security</h2>
            <p>
              Passwords are hashed with bcrypt. All connections use TLS. JWTs expire after 8 hours.
              We apply rate limiting to authentication endpoints to prevent brute-force attacks.
              Our platform is designed with the principle of least privilege — API endpoints enforce role-based access control.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">10. Changes to this policy</h2>
            <p>
              We may update this policy as the product evolves. Material changes will be communicated via email to registered users.
              The &ldquo;last updated&rdquo; date at the top of this page reflects the most recent revision.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground mb-3">11. Contact</h2>
            <p>
              Questions, requests, or complaints: <a href={`mailto:${CONTACT_EMAIL}`} className="text-foreground underline underline-offset-2">{CONTACT_EMAIL}</a>.
              If you are in the EEA and are unsatisfied with our response, you have the right to lodge a complaint with your local data protection authority.
            </p>
          </div>

        </section>

        <div className="mt-16 pt-8 border-t border-border/80 flex gap-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">← Back to home</Link>
          <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms of Service</Link>
        </div>
      </main>
    </div>
  );
}
